import type { FundSummary } from '@nemo/api';
import { stageChoicesOf, type ListStatus, type StageChoice } from '@nemo/core/model';
import { chequeWrite } from '../grid/fundColumns.js';
import { idsForLabels, type FundLists, type FundWrite } from '../grid/fundWrites.js';
import { parseChequeText, parseListText } from '../grid/paste.js';
import type { FundWriter } from '../grid/useFundWriter.js';
import type { UndoStack } from '../grid/useUndoStack.js';
import { useToast } from '../ui/Toast.js';

/**
 * Every value the pane can change, as one function each. They all go through
 * the writer the grid writes with, so a `change` row lands the same way and
 * undo — which lives on the grid's toolbar — still reaches an edit made here.
 */
export interface FundEdits {
  name: (text: string) => void;
  website: (text: string) => void;
  notes: (text: string) => void;
  stages: (choices: readonly StageChoice[]) => void;
  sectors: (ids: readonly string[]) => void;
  owners: (ids: readonly string[]) => void;
  cheque: (text: string) => void;
  notableInvestments: (text: string) => void;
  listStatus: (status: ListStatus) => void;
}

export function useFundEdits(
  fund: FundSummary,
  lists: FundLists,
  writer: FundWriter,
  undoStack: UndoStack,
): FundEdits {
  const toast = useToast();

  async function commit(next: FundWrite, before: FundWrite): Promise<void> {
    try {
      await writer.apply(fund.id, next);
    } catch {
      return;
    }
    undoStack.push({
      undo: () => writer.apply(fund.id, before),
      redo: () => writer.apply(fund.id, next),
    });
  }

  return {
    name: (text) => {
      const trimmed = text.trim();
      if (trimmed === '' || trimmed === fund.name) return;
      void commit(
        { kind: 'edits', edits: [{ column: 'name', value: trimmed }] },
        { kind: 'edits', edits: [{ column: 'name', value: fund.name }] },
      );
    },

    website: (text) => {
      const value = text.trim() === '' ? null : text.trim();
      if (value === fund.website) return;
      void commit(
        { kind: 'edits', edits: [{ column: 'website', value }] },
        { kind: 'edits', edits: [{ column: 'website', value: fund.website }] },
      );
    },

    notes: (text) => {
      if (text === (fund.notes ?? '')) return;
      void commit(
        { kind: 'edits', edits: [{ column: 'notes', value: text }] },
        { kind: 'edits', edits: [{ column: 'notes', value: fund.notes }] },
      );
    },

    stages: (choices) =>
      void commit(
        { kind: 'stages', stages: choices },
        { kind: 'stages', stages: stageChoicesOf(fund) },
      ),

    sectors: (ids) =>
      void commit(
        { kind: 'sectors', sectorIds: ids },
        { kind: 'sectors', sectorIds: idsForLabels(fund.sectors, lists.sectors) },
      ),

    owners: (ids) =>
      void commit(
        { kind: 'owners', memberIds: ids },
        { kind: 'owners', memberIds: idsForLabels(fund.owners, lists.members) },
      ),

    cheque: (text) => {
      const parsed = parseChequeText(text);
      if (!parsed.ok) {
        toast(parsed.reason);
        return;
      }
      void commit(
        chequeWrite(parsed.value),
        chequeWrite({ min: fund.chequeMin, max: fund.chequeMax, currency: fund.chequeCurrency }),
      );
    },

    notableInvestments: (text) => {
      const parsed = parseListText(text);
      if (!parsed.ok) {
        toast(parsed.reason);
        return;
      }
      void commit(
        { kind: 'edits', edits: [{ column: 'notableInvestments', value: parsed.value }] },
        {
          kind: 'edits',
          edits: [{ column: 'notableInvestments', value: fund.notableInvestments }],
        },
      );
    },

    listStatus: (status) => {
      if (status === fund.listStatus) return;
      void commit(
        { kind: 'edits', edits: [{ column: 'listStatus', value: status }] },
        { kind: 'edits', edits: [{ column: 'listStatus', value: fund.listStatus }] },
      );
    },
  };
}
